import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import putBusinessProfile from "../apis/putBusinessProfile";
import { fetchBusinessDetails } from "../store/profileSlice";
import {
  useBusinessProfileForm,
  defaultData,
  validateEditProfileFields,
} from "./profile/useBusinessProfileForm";
import PageHeader, { PageInfoHint, PageShell } from "./shared/PageHeader";
import { buildBusinessProfilePayload } from "../utils/businessProfilePayload";
import { isBusinessProfileEmpty } from "../utils/isBusinessProfileEmpty";
import { useAuthModal, ONBOARDING_AUTH_MODAL } from "../context/AuthModalContext";
import Loader from "./loader/Loader";

const notify = (msg) => toast(msg);

function formatLocationWithCountry(location, country) {
  const loc = String(location ?? "").trim();
  const c = String(country ?? "").trim();
  if (loc && c) return `${loc}, ${c}`;
  if (loc) return loc;
  if (c) return c;
  return "Not provided";
}

const ProfileView = ({ data, onEdit }) => {
  const safeData = { ...defaultData, ...data };
  const keywordList = Array.isArray(safeData.keywords)
    ? safeData.keywords.map((k) => String(k).trim()).filter(Boolean)
    : String(safeData.keywords || "")
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      notify(`Copied: ${value}`);
    } catch (e) {
      notify("Unable to copy right now");
    }
  };

  return (
    <PageShell>
      <PageHeader
        title="Business Profile"
        description="A structured view of your brand identity."
        actions={
          <button
            type="button"
            className="btn btn-dark-outline-app shrink-0"
            onClick={onEdit}
          >
            <i className="bi bi-pencil-square me-2"></i>
            Edit
          </button>
        }
      >
        <PageInfoHint title="Your business profile helps align AI-generated campaigns with your brand voice, audience, and goals." />
      </PageHeader>

      <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 text-white">
            <i className="bi bi-building"></i>
          </div>
          <div>
            <h5 className="mb-0 text-lg font-semibold text-slate-900">
              {safeData.name || "Business Name"}
            </h5>
            <p className="mb-0 text-sm text-slate-600">
              {safeData.tagline || "Your tagline appears here."}
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          {safeData.description ||
            "Add a description to explain your brand voice and value proposition."}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <i className="bi bi-grid text-slate-700"></i>
          <h6 className="mb-0 text-sm font-semibold uppercase tracking-wide text-slate-600">
            Business Details
          </h6>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <i className="bi bi-briefcase me-1"></i> Industry
            </p>
            <p className="mb-0 text-sm text-slate-800">{safeData.industry || "Not provided"}</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <i className="bi bi-diagram-3 me-1"></i> Category
            </p>
            <p className="mb-0 text-sm text-slate-800">{safeData.category || "Not provided"}</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <i className="bi bi-globe me-1"></i> Website
            </p>
            <p className="mb-0 text-sm text-slate-800">{safeData.website || "Not provided"}</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <i className="bi bi-geo-alt me-1"></i> Location
            </p>
            <p className="mb-0 text-sm text-slate-800">
              {formatLocationWithCountry(safeData.location, safeData.country)}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <i className="bi bi-tags me-1"></i> Keywords
          </p>
          {keywordList.length ? (
            <div className="flex flex-wrap gap-2">
              {keywordList.map((kw) => (
                <div
                  key={kw}
                  className="flex items-center gap-1 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700"
                >
                  <span>{kw}</span>
                  <button
                    type="button"
                    className="rounded p-0.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                    onClick={() => copyText(kw)}
                    title="Copy keyword"
                  >
                    <i className="bi bi-copy"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mb-0 text-sm text-slate-600">No keywords added yet.</p>
          )}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <i className="bi bi-stars me-1"></i> Uniqueness
          </p>
          <p className="mb-0 text-sm text-slate-800">{safeData.uniqueness || "Not provided"}</p>
        </div>
      </section>
    </PageShell>
  );
};

function EditProfileForm({ initialData, onSubmit, onCancel }) {
  const dispatch = useDispatch();
  const form = useBusinessProfileForm({ initialData, mode: "edit" });
  const { isReadOnly, renderField } = form;
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateEditProfileFields(form.formData);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = buildBusinessProfilePayload(form.formData);

    try {
      await putBusinessProfile(payload);
      notify("Business profile updated!");
      await dispatch(fetchBusinessDetails()).unwrap();
      onSubmit?.(payload);
    } catch (err) {
      console.error(err);
      notify("Oops, something went wrong!");
    }
  };

  return (
    <PageShell>
      <PageHeader title="Business Profile" />

      <form
        onSubmit={handleSubmit}
        className="my-4 w-full"
      >
        <div className="row px-2">
          {renderField("Business Name", "name", "text", 1, 12, "building", {
            fieldError: fieldErrors.name,
            required: true,
          })}
          {renderField("Description (Optional)", "description", "text", 3, 12, "card-text", {
            fieldError: fieldErrors.description,
          })}
          {renderField("Website (Optional)", "website", "url", 1, 12, "globe", {
            fieldError: fieldErrors.website,
          })}

          <div className="">
            {!isReadOnly && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  className="btn btn-dark-outline-app px-3 py-1.5 text-xs font-semibold md:px-4 md:py-2 md:text-sm"
                >
                  Update Profile <i className="bi bi-check2-circle ms-1" aria-hidden />
                </button>
                <button
                  type="button"
                  className="btn btn-secondary-app px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-sm"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </PageShell>
  );
}

export const Profile = () => {
  const user = useSelector((state) => state.auth.user);
  const { data, loading } = useSelector((state) => state.profile);
  const { openAuthModal } = useAuthModal();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!loading && isBusinessProfileEmpty(data)) {
      openAuthModal(ONBOARDING_AUTH_MODAL);
    }
  }, [user, loading, data, openAuthModal]);

  if (loading) {
    return (
      <PageShell>
        <Loader isLoading />
      </PageShell>
    );
  }

  if (isBusinessProfileEmpty(data)) {
    return (
      <PageShell>
        <PageHeader title="Business Profile" />
      </PageShell>
    );
  }

  if (!isEditing) {
    return <ProfileView data={data} onEdit={() => setIsEditing(true)} />;
  }

  return (
    <EditProfileForm
      initialData={data}
      onSubmit={() => setIsEditing(false)}
      onCancel={() => setIsEditing(false)}
    />
  );
};

export default Profile;
